import { FC } from "react";
import { Layer, Source } from "react-map-gl";
import { store } from 'stores/Store';

interface MapPinsSourceAndLayersProps {
  id: string;
  type: "geojson";
  data?: GeoJSON.FeatureCollection<GeoJSON.Point, {
    id: string; selected: boolean
  }>;
  cluster: boolean;
  clusterMaxZoom: number;
  clusterRadius: number;
  selectedPinId?: number | string;
}

export const MapPinsSourceAndLayers: FC<MapPinsSourceAndLayersProps> = ({
  id, type, data, cluster, clusterMaxZoom, clusterRadius, selectedPinId
}) => {
  return (
    <Source
      id={id}
      type={type}
      data={data}
      cluster={cluster}
      clusterMaxZoom={clusterMaxZoom}
      clusterRadius={clusterRadius}
    >
      {/* Cluster Color */}
      <Layer
        id="clusters"
        type="circle"
        filter={['has', 'point_count']}
        paint={{
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            100,
            '#f1f075',
            750,
            '#f28cb1'
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff'
        }}
      />
      {/* Cluster Text */}
      <Layer
        id="cluster-count"
        type="symbol"
        filter={['has', 'point_count']}
        layout={{
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }}
        paint={{
          'text-color': '#000000'
        }}
      />
      {/* Individual */}
      <Layer
        id="unclustered-point"
        type="symbol"
        filter={[
          'all',
          ['!=', ["get", "id"], "start"],
          ['!=', ["get", "id"], "end"],
          ["!", ["in", ["get", "id"], ["literal", store?.selectedPractitioners
            .map((s) => s.id)
            .filter((s) => s) || []
          ]]],
          ['!', ['has', 'point_count']],
          ['!=', ['get', 'id'], (selectedPinId || null)],
        ]}
        layout={{
          'icon-image': "custom-marker",
          'icon-size': .8,
          "icon-allow-overlap": true,
        }}
        paint={{
          "icon-color": ["get", "color"]
        }}
      />

    </Source>
  );
};

interface MapRouteSourceAndLayersProps {
  id: string;
  type: "geojson";
  data?: GeoJSON.Feature<GeoJSON.LineString>;
}

export const MapRouteSourceAndLayers: FC<MapRouteSourceAndLayersProps> = ({
  id, type, data,
}) => {
  return (
    <Source
      id={id}
      type={type}
      data={data}>
      <Layer
        id="route-line"
        type="line"
        layout={{
          'line-join': 'round',
          'line-cap': 'round'
        }}
        paint={{
          'line-color': '#0066FF',
          'line-width': 4
        }}
      />
    </Source>

  )
}

interface MapRoutePinsSourceAndLayersProps {
  id: string;
  type: "geojson";
  cluster: boolean;
  data?: GeoJSON.FeatureCollection<GeoJSON.Point, { id: number | string }>;
}

export const MapRoutePinsSourceAndLayers: FC<MapRoutePinsSourceAndLayersProps> = ({
  id, type, cluster, data
}) => {
  return (
    <Source
      id={id}
      type={type}
      cluster={cluster}
      data={data}>
      <Layer
        id="route-points"
        type="symbol"
        filter={[
          'all',
        ]}
        layout={{
          'text-field': [
            "number-format",
            ["+",
              ["index-of",
                ["get", "id"],
                ["literal", store?.selectedPractitioners
                  .filter((s) => !s.isFirstLocation && !s.isLastLocation)
                  .map((s) => s.id) || []]],
              store?.selectedPractitioners.some(p => p.isFirstLocation) ? 2 : 1,
            ],
            {},
          ],
          'icon-image': "custom-marker-route",
          'icon-size': 1,
          "icon-allow-overlap": true,
          'text-allow-overlap': true,
          // Text positioning - adjust these values to move the number
          'text-offset': [0, -0.2], // Moving text up by ~2px (0.1em is roughly 1.6px at 16px font size)
          'text-anchor': 'center', // Options: center, left, right, top, bottom, top-left, top-right, bottom-left, bottom-right
          // Font styling for better readability
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'], // Bold fonts
          'text-size': 16, // Increase size for better readability
        }}
        paint={{
          'text-color': '#000000', // Black text
          // Add text halo for better contrast
          'text-halo-color': 'rgba(255, 255, 255, 0.9)', // White halo around black text
          'text-halo-width': 2, // Width of the halo
          'text-halo-blur': 1, // Slight blur for softer edge
        }}
      />
    </Source>
  );
}

interface MapStartEndLocationSourceAndLayersProps {
  id: string;
  type: "geojson";
  data: GeoJSON.FeatureCollection<GeoJSON.Point, { id: string; type: 'start' | 'end' }>;
}

export const MapStartEndLocationSourceAndLayers: FC<MapStartEndLocationSourceAndLayersProps> = (
  { id, type, data }
) => {
  return (
    <Source
      id={id}
      type={type}
      data={data}
    >
      {/* Start location markers */}
      <Layer
        id="start-location-points"
        type="symbol"
        filter={['==', ['get', 'type'], 'start']}
        layout={{
          'text-field': '1',
          'icon-image': "custom-marker-route",
          'icon-size': 1,
          "icon-allow-overlap": true,
          'text-allow-overlap': true,
          'text-offset': [0, -0.2], // Moving text up more
          'text-anchor': 'center',
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 16,
        }}
        paint={{
          'text-color': '#000000', // Black text
          'text-halo-color': 'rgba(255, 255, 255, 0.9)', // White halo
          'text-halo-width': 2,
          'text-halo-blur': 1,
        }}
      />
      <Layer
        id="end-location-points"
        type="symbol"
        filter={['==', ['get', 'type'], 'end']}
        layout={{
          'text-field': String(store?.selectedPractitioners.length),
          'icon-image': "custom-marker-route",
          'icon-size': 1,
          "icon-allow-overlap": true,
          'text-allow-overlap': true,
          'text-offset': [0, -0.2], // Moving text up more
          'text-anchor': 'center',
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 16,
        }}
        paint={{
          'text-color': '#000000', // Black text
          'text-halo-color': 'rgba(255, 255, 255, 0.9)', // White halo
          'text-halo-width': 2,
          'text-halo-blur': 1,
        }}
      />
    </Source>
  );
};

// Separate component for selected pin overlay - renders on top of everything
interface SelectedPinOverlayProps {
  id: string;
  type: "geojson";
  data?: GeoJSON.FeatureCollection<GeoJSON.Point, {
    id: number | string; [key: string]: any;
  }>;
  selectedPinId?: number | string;
}

interface SearchResultPinsSourceAndLayersProps {
  id: string;
  type: "geojson";
  data?: GeoJSON.FeatureCollection<GeoJSON.Point, {
    id: string; selected: boolean
  }>;
  selectedPinId?: string;
}

export const SearchResultPinsSourceAndLayers: FC<SearchResultPinsSourceAndLayersProps> = ({
  id, type, data, selectedPinId
}) => {
  return (
    <Source
      id={id}
      type={type}
      data={data}
    >
      <Layer
        id="search-result-point"
        type="symbol"
        layout={{
          'icon-image': "custom-search-marker",
          'icon-size': .8,
          "icon-allow-overlap": true,
        }}
        paint={{
          "icon-color": ["get", "color"]
        }}
      />

    </Source>
  );
};

export const SelectedPinOverlay: FC<SelectedPinOverlayProps> = ({
  id, type, data, selectedPinId
}) => {
  return (
    <Source
      id={`${id}-selected`}
      type={type}
      data={data}
    >
      {/* Selected pin - renders on top of all other layers */}
      <Layer
        id={`${id}-selected-point`}
        type="symbol"
        filter={[
          "all",
          ['!', ['has', 'point_count']],
          ['==', ['get', 'id'], (selectedPinId || null)],
        ]}
        layout={{
          'icon-image': "selected-marker-green",
          'icon-size': .9,
          "icon-allow-overlap": true,
        }}
      />
    </Source>
  );
};
